import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type {
  CatalogueMigration,
  StockCutoverReport,
  StockReconciliationDay,
} from "@shared-core/data/api-types";
import { formatCount, formatDateOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../app/components/primitives/card";
import { Label } from "../../app/components/primitives/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { selectClassName } from "../stock/StockFormField";
import { StartShadowDialog } from "./StartShadowDialog";

type LoadStatus = "loading" | "ready" | "none" | "error";

/**
 * Staging only: the customer's shadow against Tharstern's copy. Before the
 * shadow starts, the way to start it; after, the daily stock reconciliation.
 */
export function ShadowTab({
  migration,
  onStarted,
}: {
  migration: CatalogueMigration;
  /** The shadow started: the page re-reads the migration. */
  onStarted: (message: string) => void;
}) {
  const dataAdapter = useDataAdapter();
  const customerId = migration.customer_id;
  const [isStarting, setIsStarting] = useState(false);
  const [runDate, setRunDate] = useState<string | undefined>(undefined);
  const [day, setDay] = useState<StockReconciliationDay | null>(null);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const hasShadow = migration.cutover_state !== null;

  useEffect(() => {
    if (!hasShadow) return;
    let cancelled = false;
    setStatus("loading");
    dataAdapter
      .getStockReconciliationDay(customerId, runDate)
      .then((result) => {
        if (cancelled) return;
        setDay(result);
        setStatus(result ? "ready" : "none");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock_cutover.reconciliation.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, customerId, runDate, hasShadow]);

  if (!hasShadow) {
    return (
      <div className="space-y-3 text-sm p-6 rounded-lg border border-border">
        <p>
          The stock shadow hasn't started for this customer. The shadow will
          import the customer's stock balances from Tharstern's REFRESH copy
          into One on staging and reconcile the balances every day. Nothing will
          be ordered.
        </p>
        <Button size="sm" variant="outline" onClick={() => setIsStarting(true)}>
          Start shadow
        </Button>
        {isStarting && (
          <StartShadowDialog
            customerId={customerId}
            customerName={migration.customer_name}
            tharsternCustomerCode={migration.tharstern_customer_code}
            onClose={() => setIsStarting(false)}
            onStarted={(message) => {
              setIsStarting(false);
              onStarted(message);
            }}
          />
        )}
      </div>
    );
  }
  return (
    <StockReconciliationView
      status={status}
      day={day}
      onRunDateChange={setRunDate}
    />
  );
}

function StockReconciliationView({
  status,
  day,
  onRunDateChange,
}: {
  status: LoadStatus;
  day: StockReconciliationDay | null;
  onRunDateChange: (runDate: string) => void;
}) {
  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-muted-foreground">Loading reconciliation…</div>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div
        role="alert"
        className="flex items-center gap-2 text-sm p-4 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300"
      >
        <AlertTriangle className="size-4 shrink-0" />
        Couldn't load the reconciliation. Please try again.
      </div>
    );
  }
  if (status === "none" || day == null) {
    return (
      <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
        No reconciliation has run for this customer yet. The first runs the
        morning after its shadow starts.
      </div>
    );
  }

  const differing = day.differing_lines;

  return (
    <div className="space-y-6">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="space-y-1">
          <Label htmlFor="reconciliation-date">Day</Label>
          <select
            id="reconciliation-date"
            className={`${selectClassName} w-auto`}
            value={day.run_date}
            onChange={(e) => onRunDateChange(e.target.value)}
          >
            {day.run_dates.map((d) => (
              <option key={d} value={d}>
                {formatDateOrDash(d)}
              </option>
            ))}
          </select>
        </div>
        <ReportButton reportUrl={day._self} />
      </div>

      <section
        aria-label="Daily reconciliation"
        className="grid gap-3 grid-cols-2 lg:grid-cols-5"
      >
        <Tile
          label="Balances match"
          value={`${formatCount(day.balances_matched)}/${formatCount(day.balances_total)}`}
          isGood={day.balances_matched === day.balances_total}
          link={differing.length > 0 ? "#differing-items" : undefined}
        />
        <Tile
          label="Despatches matched"
          value={`${formatCount(day.despatches_matched)}/${formatCount(day.despatches_total)}`}
          isGood={day.despatches_matched === day.despatches_total}
        />
        <Tile
          label="Reprints proposed within 1 working day"
          value={
            day.reprints
              ? `${formatCount(day.reprints.proposed_within_one_working_day)}/${formatCount(day.reprints.total)}`
              : "Not measured (S6)"
          }
          isGood={
            day.reprints
              ? day.reprints.proposed_within_one_working_day ===
                day.reprints.total
              : null
          }
        />
        <Tile
          label="Unspecified items"
          value={formatCount(day.unspecified_items)}
          isGood={null}
        />
        <Tile
          label="Day clean"
          value={day.is_clean ? "Yes" : "No"}
          isGood={day.is_clean}
        />
      </section>

      <Card id="differing-items">
        <CardHeader>
          <CardTitle>Differing items</CardTitle>
        </CardHeader>
        <CardContent>
          {differing.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every balance and despatch matched Tharstern on this day.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">On hand in One</TableHead>
                    <TableHead className="text-right">
                      Tharstern balance
                    </TableHead>
                    <TableHead className="text-right">
                      Book-outs in One
                    </TableHead>
                    <TableHead className="text-right">
                      Tharstern despatches
                    </TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {differing.map((line) => (
                    <TableRow key={line.product_code}>
                      <TableCell className="font-medium">
                        <Link
                          to={paths.stockItemDetail({
                            stockItemId: line.stock_item_id,
                          })}
                          className="hover:underline"
                        >
                          {line.product_code}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {line.cis_on_hand == null
                          ? "—"
                          : formatCount(line.cis_on_hand)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {line.tharstern_balance == null
                          ? "—"
                          : formatCount(line.tharstern_balance)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCount(line.cis_book_outs)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCount(line.tharstern_despatches)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="error">
                          {line.status === "differs"
                            ? "Differs"
                            : "Tharstern moved after cutover"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Follows the day's own report link (its `_self`). */
function ReportButton({ reportUrl }: { reportUrl: string }) {
  const dataAdapter = useDataAdapter();
  const [report, setReport] = useState<StockCutoverReport | null>(null);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    setReport(null);
    setHasFailed(false);
  }, [reportUrl]);

  async function load() {
    try {
      setReport(await dataAdapter.getStockCutoverReport(reportUrl));
    } catch (error) {
      console.error("stock_cutover.report.load_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setHasFailed(true);
    }
  }

  return (
    <div className="w-full space-y-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => (report ? setReport(null) : void load())}
      >
        {report ? "Hide the report" : "Show the day's report"}
      </Button>
      {hasFailed && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          Couldn't load the report.
        </p>
      )}
      {report && (
        <pre className="text-xs whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 overflow-x-auto">
          {report.markdown}
        </pre>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  isGood,
  link,
}: {
  label: string;
  value: string;
  isGood: boolean | null;
  link?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-1.5 text-lg font-medium tabular-nums">
        {isGood === true && (
          <CheckCircle2
            className="size-4 text-green-600 dark:text-green-400"
            aria-hidden
          />
        )}
        {isGood === false && (
          <XCircle
            className="size-4 text-red-600 dark:text-red-400"
            aria-hidden
          />
        )}
        {value}
      </div>
      {link && (
        <a href={link} className="text-xs underline text-muted-foreground">
          See the differing items
        </a>
      )}
    </div>
  );
}
